import { useQuery } from 'react-query';
import { listConversations, type Conversation } from '@/services/conversationsService';
import type { ChannelType } from '@/services/channelsService';

export interface UseConversationsParams {
  channel?: ChannelType;
  status?: 'open' | 'closed';
  page?: number;
  limit?: number;
  enabled?: boolean;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export function useConversations(params: UseConversationsParams = {}) {
  const { channel, status, page = 1, limit = 20, enabled = true } = params;

  return useQuery<PaginatedResponse<Conversation>>({
    queryKey: ['conversations', { channel, status, page, limit }],
    queryFn: async () => {
      const res = await listConversations({
        channel,
        status,
        page,
        limit,
      });
      // Backend retorna { success: true, data: Conversation[], pagination: {...} }
      // Normalizamos para { data: [...], pagination }
      const api = res as unknown as {
        success: boolean;
        data: Conversation[] | PaginatedResponse<Conversation>;
        pagination?: PaginatedResponse<Conversation>['pagination'];
      };

      // Se já vier no formato { data, pagination }
      if (api && (api.data as any)?.data && (api.data as any)?.pagination) {
        return api.data as PaginatedResponse<Conversation>;
      }

      // Caso comum: data é um array e a paginação vem no topo
      return {
        data: (api.data as Conversation[]) ?? [],
        pagination: api.pagination ?? { page, limit, total: (api.data as Conversation[])?.length ?? 0, pages: 1 },
      } satisfies PaginatedResponse<Conversation>;
    },
    keepPreviousData: true,
    enabled,
  });
}
